import { useEffect, useState } from "react";
import { useProjects } from "../lib/ProjectContext";
import { api, ApiError } from "../lib/apiClient";
import { useCurrentAppUser } from "../lib/useCurrentAppUser";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import type { Escalation } from "@shared/types";

/**
 * "Ask your team" — questions the AI couldn't answer (or an inspector
 * flagged as wrong) that other people on this project can weigh in on.
 * Scoped to the selected project the same way Ask/Sources/Contract Docs
 * already are, via ProjectContext rather than a URL param.
 */
export function EscalationsPage() {
  const { selectedProjectId, projects, loading: projectsLoading } = useProjects();
  const { user } = useCurrentAppUser();
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showAskForm, setShowAskForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);

  function load() {
    if (!selectedProjectId) return;
    setLoading(true);
    api
      .get<{ escalations: Escalation[] }>(`/api/projects/${selectedProjectId}/escalations`)
      .then((data) => setEscalations(data.escalations))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load team questions."))
      .finally(() => setLoading(false));
  }

  useEffect(load, [selectedProjectId]);

  async function askTeam(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedProjectId || !newQuestion.trim()) return;
    setAsking(true);
    setAskError(null);
    try {
      await api.post(`/api/projects/${selectedProjectId}/escalations`, { question: newQuestion.trim() });
      setNewQuestion("");
      setShowAskForm(false);
      load();
    } catch (err) {
      setAskError(err instanceof ApiError ? err.message : "Failed to post your question.");
    } finally {
      setAsking(false);
    }
  }

  async function respond(escalationId: string) {
    const responseText = (drafts[escalationId] ?? "").trim();
    if (!responseText) return;
    setBusyId(escalationId);
    setError(null);
    try {
      await api.post(`/api/escalations/${escalationId}/respond`, { responseText });
      setDrafts((prev) => ({ ...prev, [escalationId]: "" }));
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to send your response.");
    } finally {
      setBusyId(null);
    }
  }

  async function resolve(escalationId: string) {
    setBusyId(escalationId);
    setError(null);
    try {
      await api.post(`/api/escalations/${escalationId}/resolve`);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to resolve.");
    } finally {
      setBusyId(null);
    }
  }

  if (projectsLoading) return <p className="muted">Loading…</p>;
  if (projects.length === 0) {
    return (
      <div className="card">
        <p>No projects yet. Ask an owner to create one, or (if you're the owner) use the Sources screen to get started.</p>
      </div>
    );
  }
  if (loading) return <p className="muted">Loading team questions…</p>;

  return (
    <div className="content-col-wide">
      <div className="sources-head">
        <p className="sources-desc" style={{ marginTop: 0 }}>
          Questions the AI couldn't answer, or that got flagged as wrong, land here automatically — but you don't
          need either to start one. Ask a colleague anything directly. Visible to everyone on this project, in-app
          only for now, so check back rather than expecting a notification.
        </p>
        {!showAskForm && (
          <button className="btn btn-primary" type="button" onClick={() => setShowAskForm(true)}>
            Ask your team
          </button>
        )}
      </div>

      {showAskForm && (
        <form className="card" onSubmit={askTeam}>
          <div className="field" style={{ marginBottom: "0.6rem" }}>
            <label htmlFor="new-question">New question for the team</label>
            <textarea
              id="new-question"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              placeholder="e.g. Has anyone dealt with a similar cover-depth conflict on a past ODOT contract?"
              autoFocus
            />
          </div>
          {askError && <p className="error-text">{askError}</p>}
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button className="btn btn-primary" type="submit" disabled={asking || !newQuestion.trim()}>
              {asking ? "Posting…" : "Post question"}
            </button>
            <button
              className="btn"
              type="button"
              onClick={() => {
                setShowAskForm(false);
                setNewQuestion("");
                setAskError(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {error && <p className="error-text">{error}</p>}

      {escalations.length === 0 ? (
        <div className="card">
          <p className="muted">Nothing escalated yet on this project.</p>
        </div>
      ) : (
        escalations.map((esc) => {
          const canResolve = esc.status === "open" && (esc.raisedByEmail === user?.email || user?.role === "owner");
          return (
            <div className="card escalation-card" key={esc.id}>
              <div className="escalation-head">
                <span className={`status-chip ${esc.status === "open" ? "status-draft" : "status-approved"}`}>
                  {esc.status === "open" ? "Open" : "Resolved"}
                </span>
                <span className="muted" style={{ fontSize: "0.8rem" }}>
                  {esc.raisedByEmail} · {formatRelativeTime(esc.createdAt)}
                </span>
              </div>
              <p className="directive-text" style={{ fontSize: "0.95rem" }}>
                {esc.question}
              </p>
              {esc.directive && <p className="citation-excerpt">AI's answer: {esc.directive}</p>}
              {esc.note && <p className="citation-excerpt">Note from {esc.raisedByEmail}: {esc.note}</p>}

              {esc.responses.length > 0 && (
                <div className="escalation-responses">
                  {esc.responses.map((r) => (
                    <div className="escalation-response" key={r.id}>
                      <div className="escalation-response-meta">
                        {r.responderEmail} · {formatRelativeTime(r.createdAt)}
                      </div>
                      <div>{r.responseText}</div>
                    </div>
                  ))}
                </div>
              )}

              {esc.status === "open" && (
                <div className="escalation-reply">
                  <textarea
                    value={drafts[esc.id] ?? ""}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [esc.id]: e.target.value }))}
                    placeholder="Share what you know…"
                  />
                  <div className="escalation-reply-actions">
                    <button className="btn btn-primary" disabled={busyId === esc.id} onClick={() => respond(esc.id)}>
                      {busyId === esc.id ? "Sending…" : "Reply"}
                    </button>
                    {canResolve && (
                      <button className="btn" disabled={busyId === esc.id} onClick={() => resolve(esc.id)}>
                        Mark resolved
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
